import { BuildCancelledError, throwIfAborted } from "@map2threejs/core/errors";
import type { BuildingContext, BuildingFeature, MapFeatureSet } from "@map2threejs/core/types";

interface PendingRequest {
  resolve(value: unknown): void;
  reject(reason: unknown): void;
  cleanup(): void;
}

interface DecoderResponse {
  id: number;
  ok: boolean;
  result?: unknown;
  error?: string;
}

export class TileDecoderClient {
  private worker: Worker | undefined;
  private nextId = 1;
  private readonly pending = new Map<number, PendingRequest>();

  private stopWorker(reason: Error): void {
    this.worker?.terminate();
    this.worker = undefined;
    for (const request of this.pending.values()) {
      request.cleanup();
      request.reject(reason);
    }
    this.pending.clear();
  }

  private getWorker(): Worker | undefined {
    if (typeof Worker === "undefined") return undefined;
    if (!this.worker) {
      this.worker = new Worker(new URL("./decoder.worker.ts", import.meta.url), { type: "module" });
      this.worker.onmessage = (event: MessageEvent<DecoderResponse>) => {
        const request = this.pending.get(event.data.id);
        if (!request) return;
        this.pending.delete(event.data.id);
        request.cleanup();
        if (event.data.ok) request.resolve(event.data.result);
        else request.reject(new Error(event.data.error ?? "Tile decoder failed."));
      };
      this.worker.onerror = (event) => {
        this.stopWorker(new Error(event.message || "Tile decoder worker failed."));
      };
      this.worker.onmessageerror = () => this.stopWorker(new Error("Tile decoder worker returned unreadable data."));
    }
    return this.worker;
  }

  private request<T>(
    message: Record<string, unknown>,
    transfer: Transferable[],
    signal: AbortSignal,
  ): Promise<T> {
    throwIfAborted(signal);
    const worker = this.getWorker();
    if (!worker) throw new Error("Worker unavailable");
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      const cancel = (): void => this.stopWorker(new BuildCancelledError());
      signal.addEventListener("abort", cancel, { once: true });
      this.pending.set(id, {
        resolve: (value) => resolve(value as T),
        reject,
        cleanup: () => signal.removeEventListener("abort", cancel),
      });
      try {
        worker.postMessage({ ...message, id }, transfer);
      } catch (error) {
        const request = this.pending.get(id);
        request?.cleanup();
        this.pending.delete(id);
        reject(error);
      }
    });
  }

  async decodeTerrain(
    blob: Blob,
    signal: AbortSignal,
  ): Promise<{ width: number; height: number; elevations: Float32Array }> {
    throwIfAborted(signal);
    const worker = this.getWorker();
    if (!worker) {
      const { decodeTerrainBlob } = await import("./decoder");
      const decoded = await decodeTerrainBlob(blob);
      throwIfAborted(signal);
      return decoded;
    }
    const buffer = await blob.arrayBuffer();
    return await this.request({ type: "terrain", buffer, mimeType: blob.type }, [buffer], signal);
  }

  async decodeBuildings(
    buffer: ArrayBuffer,
    z: number,
    x: number,
    y: number,
    signal: AbortSignal,
  ): Promise<BuildingFeature[]> {
    throwIfAborted(signal);
    const worker = this.getWorker();
    if (!worker) {
      const { decodeBuildingMvt } = await import("./decoder");
      const decoded = decodeBuildingMvt(buffer, z, x, y);
      throwIfAborted(signal);
      return decoded;
    }
    return await this.request({ type: "buildings", buffer, z, x, y }, [buffer], signal);
  }

  async decodeBuildingContext(
    buffer: ArrayBuffer,
    z: number,
    x: number,
    y: number,
    signal: AbortSignal,
  ): Promise<BuildingContext> {
    throwIfAborted(signal);
    const worker = this.getWorker();
    if (!worker) {
      const { decodeBuildingContextMvt } = await import("./decoder");
      const decoded = decodeBuildingContextMvt(buffer, z, x, y);
      throwIfAborted(signal);
      return decoded;
    }
    return await this.request({ type: "building-context", buffer, z, x, y }, [buffer], signal);
  }

  async decodeMapFeatures(
    buffer: ArrayBuffer,
    z: number,
    x: number,
    y: number,
    signal: AbortSignal,
  ): Promise<Pick<MapFeatureSet, "roads" | "waterways" | "barriers" | "infrastructure" | "surfaces" | "streetObjects">> {
    throwIfAborted(signal);
    const worker = this.getWorker();
    if (!worker) {
      const { decodeMapFeaturesMvt } = await import("./decoder");
      const decoded = decodeMapFeaturesMvt(buffer, z, x, y);
      throwIfAborted(signal);
      return decoded;
    }
    return await this.request({ type: "map-features", buffer, z, x, y }, [buffer], signal);
  }

  dispose(): void {
    this.stopWorker(new Error("Tile decoder disposed."));
  }
}
